<script lang="ts" setup>
    import { ref, onMounted } from 'vue';
    import { gsap } from 'gsap';
    import { ScrollTrigger } from 'gsap/ScrollTrigger';
    import StoryblokImage from '~/storyblok/Image/utils/StoryblokImage.vue';

    const props = defineProps<{ blok: TopStoryStoryblok }>();
    gsap.registerPlugin(ScrollTrigger);
    const parallaxWrapperRef = ref();
    const textWrapperRef = ref();
    const imageWrapperRef = ref();
    const GradientShapeRef1 = ref();
    const GradientShapeRef2 = ref();

    const mediaMatch = gsap.matchMedia();

    const endPointValue = 'bottom bottom';

    onMounted(() => {
        mediaMatch.add('(max-width: 1023px)', () => {
            gsap.from(GradientShapeRef1.value, {
                scrollTrigger: {
                    trigger: imageWrapperRef.value,
                    scrub: true,
                    start: '40% bottom',
                    end: endPointValue,
                    toggleActions: 'restart pause reverse pause'
                },
                y: 1200,
                x: -50
            });

            gsap.from(GradientShapeRef2.value, {
                scrollTrigger: {
                    trigger: imageWrapperRef.value,
                    scrub: true,
                    start: '40% bottom',
                    end: endPointValue,
                    toggleActions: 'restart pause reverse pause'
                },
                y: 600,
                x: -80
            });

            gsap.from(textWrapperRef.value, {
                scrollTrigger: {
                    trigger: textWrapperRef.value,
                    scrub: true,
                    start: 'start bottom',
                    end: endPointValue,
                    toggleActions: 'restart pause reverse pause'
                },
                y: -80
            });

            gsap.from(imageWrapperRef.value, {
                scrollTrigger: {
                    trigger: imageWrapperRef.value,
                    scrub: true,
                    start: '5% bottom',
                    end: endPointValue,
                    toggleActions: 'restart pause reverse pause'
                },
                y: -80
            });
        });

        mediaMatch.add('(min-width: 1024px)', () => {
            gsap.from(GradientShapeRef1.value, {
                scrollTrigger: {
                    trigger: parallaxWrapperRef.value,
                    scrub: 0.8,
                    start: '40% bottom',
                    end: endPointValue,
                    toggleActions: 'restart pause reverse pause'
                },
                y: 1200,
                x: 50
            });

            gsap.from(GradientShapeRef2.value, {
                scrollTrigger: {
                    trigger: parallaxWrapperRef.value,
                    scrub: 0.8,
                    start: '40% bottom',
                    end: endPointValue,
                    toggleActions: 'restart pause reverse pause'
                },
                y: 600,
                x: -80
            });

            gsap.from(textWrapperRef.value, {
                scrollTrigger: {
                    trigger: parallaxWrapperRef.value,
                    scrub: 0.8,
                    start: '40% bottom',
                    end: endPointValue,
                    toggleActions: 'restart pause reverse pause'
                },
                y: -80
            });

            gsap.from(imageWrapperRef.value, {
                scrollTrigger: {
                    trigger: parallaxWrapperRef.value,
                    scrub: 0.8,
                    start: '40% bottom',
                    end: endPointValue,
                    toggleActions: 'restart pause reverse pause'
                },
                y: 50
            });
        });
    });
</script>

<template>
    <div
        ref="parallaxWrapperRef"
        v-editable="props.blok"
        class="top-story"
    >
        <div class="max-width-wrapper">
            <div
                ref="textWrapperRef"
                class="text-wrapper"
            >
                <TextContentBlock
                    :headline="props.blok.headline"
                    :subheadline="props.blok.subheadline"
                    :text="props.blok.text"
                    :cta="props.blok.button"
                />
            </div>

            <div
                ref="imageWrapperRef"
                class="image-wrapper"
            >
                <svg
                    aria-hidden="true"
                    viewBox="0 0 1 1"
                    style="fill-rule: evenodd; clip-rule: evenodd; stroke-linejoin: round; stroke-miterlimit: 2"
                >
                    <defs>
                        <clipPath
                            id="top-story-mask"
                            clipPathUnits="objectBoundingBox"
                        >
                            <path
                                d="M0.505,1L-0,1L-0,0.489C-0,0.219 0.222,-0 0.495,0L1,0L1,0.511C1,0.781 0.778,1 0.505,1Z"
                                style="
                                    fill: white;
                                    fill-rule: nonzero;
                                    transform-origin: center;
                                    transform: rotate(90deg);
                                "
                            />
                        </clipPath>
                    </defs>
                </svg>

                <StoryblokImage
                    :media="props.blok.image"
                    type="mainPicture"
                    :alt="props.blok.image.alt ? props.blok.image.alt : ''"
                />
            </div>
        </div>

        <div class="background">
            <div
                ref="GradientShapeRef2"
                class="parallax-wrapper"
            >
                <GradientShape
                    shape="circle"
                    theme="gray"
                    gradient-angle="45deg"
                />
            </div>

            <div
                ref="GradientShapeRef1"
                class="parallax-wrapper"
            >
                <GradientShape
                    shape="leaf"
                    theme="gray"
                    gradient-angle="45deg"
                />
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/teaser/top-story.scss';
</style>
